import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  DialogActions,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Datagrid, List, Pagination, useDataProvider, useNotify, TextField as RaTextField } from 'react-admin';
import { ClientSplitFormProps, ClientSplitRule, ClientSplitRulePayload, PharmacyOption } from '../utils/DataTypes';
import { applyTrim, debounce, EMPTY_SPLIT_RULE, normalizeRules, validateCustomSql } from './clientSplitHelpers';
import ClientCrosswalkSummaryTable from './ClientCrosswalkSummaryTable';

const ClientSplitForm: React.FC<ClientSplitFormProps> = ({ primaryClient, effectiveDate, onSaveRule, isSaving }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [listFilters, setListFilters] = useState<any | null>(null);
  const [financialCutoffDate, setFinancialCutoffDate] = useState('');

  const [hasLoadedPreviewList, setHasLoadedPreviewList] = useState(false);

  const [previewAfterTable, setPreviewAfterTable] = useState<any[]>([]);

  const [pharmacyOptions, setPharmacyOptions] = useState<PharmacyOption[]>([]);
  const [pharmacyLoading, setPharmacyLoading] = useState(false);

  const [pharmacyOpen, setPharmacyOpen] = useState(false);
  const [expandChips, setExpandChips] = useState(false);
  const [selectedRows, setSelectedRows] = useState<{ [index: number]: any }>({});
  const [activeRuleIndex, setActiveRuleIndex] = useState<number | null>(null);

  const [isLoadingRules, setIsLoadingRules] = useState(true);

  const [existingRules, setExistingRules] = useState<ClientSplitRule[]>([]);
  const [newRules, setNewRules] = useState<ClientSplitRule[]>([]);

  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [ruleEntryMode, setRuleEntryMode] = useState<'ui' | 'custom_sql'>('ui');
  const [draftRule, setDraftRule] = useState<ClientSplitRule | null>(null);

  const hasManualSql = newRules.some((r) => r.ruleEntryMode === 'custom_sql');

  const customSqlError =
    ruleEntryMode === 'custom_sql' && draftRule?.customSql ? validateCustomSql(draftRule.customSql ?? '') : null;

  const DEFAULT_SPLIT_SQL = `UPDATE {{TABLE}} 
SET client_number = '99999', client_name = 'New Client Name' 
WHERE orig_client_number = '${primaryClient.client_number}' 
  AND RIGHT(LEFT(TRIM(medical_group_number), 6), 3) = 'ABC';`;

  const isDraftValid = (rule: ClientSplitRule | null): boolean => {
    if (!rule) return false;

    if (ruleEntryMode === 'custom_sql') {
      return !!rule.customSql?.trim() && validateCustomSql(rule.customSql) === null;
    }

    const hasTargetInfo = !!rule.newClientName && !!rule.newClientNumber;
    if (!hasTargetInfo) return false;

    if (rule.sourceField === 'pharmacy') {
      return !!rule.identifierType && (rule.identifierValues?.length ?? 0) > 0;
    }

    return !!rule.sourceField && !!rule.matchValue?.trim();
  };

  useEffect(() => {
    if (!primaryClient || !primaryClient.client_number) {
      setIsLoadingRules(false);
      return;
    }

    setIsLoadingRules(true);
    setExistingRules([]);
    setNewRules([]);

    dataProvider
      .getOne('ClientSplitRules', { id: primaryClient.client_number })
      .then((response: any) => {
        const fetchedRules = response?.data?.rules || [];

        if (fetchedRules.length > 0) {
          const mapped = fetchedRules.map((ruleRow: any) => {
            const config = ruleRow.extract_params || {};
            const sourceField = config.sourceField?.replace('orig_', '') || '';

            return {
              ...EMPTY_SPLIT_RULE,

              newClientName: config.newClientName,
              newClientNumber: config.newClientNumber,
              fieldToSplit: config.fieldToSplit || '',
              sourceField,
              matchValue: config.matchValue || '',
              conditionLength: config.conditionLength?.toString() ?? '',
              leftTrim: config.leftTrim?.toString() ?? '',
              rightTrim: config.rightTrim?.toString() ?? '',

              identifierType: config.identifierType,
              identifierValues: (config.identifierValues || []).map((v: string) => ({ value: v, name: v })),

              isExisting: true,
            };
          });

          setExistingRules(mapped);
        }
      })
      .catch(() => {
        notify('Failed to load existing rules.', { type: 'warning' });
        setNewRules([]);
      })
      .finally(() => setIsLoadingRules(false));
  }, [primaryClient, dataProvider, notify]);

  const CleanupEmpty = () => (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h6" paragraph color="text.secondary">
        No Preview Data Found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        There are no records matching your selected client and date.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Try adjusting the "Financial Cutoff Date (From)" to an earlier date.
      </Typography>
    </Box>
  );

  const handleFetchPreview = () => {
    setListFilters({
      client_number: primaryClient.client_number,
      financial_cutoff_date: financialCutoffDate,
    });
    setHasLoadedPreviewList(true);
  };

  useEffect(() => {
    if (!listFilters) return;

    dataProvider
      .getList('ClientCleanupPreview', {
        filter: listFilters,
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
      })
      .then((res) => {
        setPreviewData(res.data || []);
      })
      .catch(() => {
        setPreviewData([]);
      });
  }, [listFilters]);

  useEffect(() => {
    setPreviewAfterTable([]);
    setHasLoadedPreviewList(false);
  }, [primaryClient, effectiveDate]);

  useEffect(() => {
    setPreviewAfterTable([]);
  }, [newRules]);

  const fetchPharmacies = async (identifierType: 'npi' | 'nabp', searchText: string) => {
    try {
      const res = await dataProvider.getList('PharmacySearch', {
        filter: {
          q: searchText,
          identifier_type: identifierType,
        },
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'value', order: 'ASC' },
      });
      return res.data || [];
    } catch (error) {
      notify('notifications.CLIENT_SPLIT.PHARMACY.FETCH_ERROR', { type: 'warning' });
      return [];
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (type: 'npi' | 'nabp', text: string) => {
      if (!text || text.length < 2) {
        setPharmacyOptions([]);
        return;
      }
      setPharmacyLoading(true);
      const results = await fetchPharmacies(type, text);
      setPharmacyOptions(results);
      setPharmacyLoading(false);
    }, 400),
    []
  );

  const handleSaveDraftToRulesList = () => {
    if (!draftRule) return;

    const ruleToSave = {
      ...draftRule,
      ruleEntryMode,
    };

    setNewRules((prev) => {
      if (editingRuleIndex !== null) {
        const updated = [...prev];
        updated[editingRuleIndex] = ruleToSave;
        return updated;
      } else {
        return [...prev, ruleToSave];
      }
    });

    setIsRuleDialogOpen(false);
    setEditingRuleIndex(null);
    setDraftRule(null);
    setPreviewResults([]);
  };

  const formatSplitRuleSummary = (rule: ClientSplitRule): string => {
    const target = `-> ${rule.newClientName} (${rule.newClientNumber})`;

    if (rule.ruleEntryMode === 'custom_sql') {
      return `MANUAL SQL: ${rule.customSql?.substring(0, 50)}... ${target}`;
    }

    if (rule.sourceField === 'pharmacy') {
      const values = (rule.identifierValues ?? []).map((v) => v.value).join(', ');
      const typeLabel = rule.identifierType?.toUpperCase() || 'NPI/NABP';
      return `Pharmacy Split | ${typeLabel}: ${values} ${target}`;
    }

    const fieldLabel = rule.sourceField.replace(/_number/g, '').replace(/_/g, ' ');
    const opLabel = rule.matchOperator === 'contains' ? 'contains' : rule.matchOperator === 'not_equals' ? '!=' : '=';

    const extraction =
      Number(rule.leftTrim) > 0 || Number(rule.rightTrim) > 0
        ? ` | Trims: L:${rule.leftTrim || 0} R:${rule.rightTrim || 0}`
        : '';

    return `${fieldLabel} Split | Match: ${opLabel} "${rule.matchValue}"${extraction} ${target}`;
  };

  const applyRulesToRow = (row: any, rules: ClientSplitRule[]) => {
    const updated = {
      ...row,
      orig_client_number: row.client_number,
      orig_client_name: row.client_name,
    };

    for (const rule of rules) {
      if (rule.ruleEntryMode === 'custom_sql') continue;

      let isMatch = false;

      if (rule.sourceField === 'pharmacy') {
        const idType = rule.identifierType === 'nabp' ? 'pharmacy_nabp' : 'pharmacy_npi';
        const selectedIds = (rule.identifierValues ?? []).map((v) => String(v.value));

        if (selectedIds.includes(String(updated[idType]))) {
          isMatch = true;
        }
      } else {
        const originalValue = updated[rule.sourceField];
        if (!originalValue) continue;

        const transformedValue = applyTrim(originalValue, rule).trim();
        const matchTarget = (rule.matchValue || '').trim();
        const op = rule.matchOperator || 'equals';

        if (op === 'contains') {
          isMatch = transformedValue.includes(matchTarget);
        } else if (op === 'not_equals') {
          isMatch = transformedValue !== matchTarget;
        } else {
          isMatch = transformedValue === matchTarget;
        }
      }

      if (isMatch) {
        updated.client_number = rule.newClientNumber;
        updated.client_name = rule.newClientName;
        break;
      }
    }

    return updated;
  };

  const handlePreviewRules = () => {
    if (previewData.length === 0) {
      notify('Please view raw data first.', { type: 'warning' });
      return;
    }

    const allTransformed = previewData.map((row) => applyRulesToRow(row, newRules));

    const onlyMoved = allTransformed.filter((row) => row.client_number !== row.orig_client_number);

    setPreviewAfterTable(onlyMoved);

    if (onlyMoved.length === 0) {
      notify('No matches found for these rules in the current data.', { type: 'info' });
    }
  };

  const handleSave = () => {
    const allRules = [...existingRules, ...newRules];

    for (let i = 0; i < allRules.length; i++) {
      const r = allRules[i];

      if (!r.newClientName || !r.newClientNumber) {
        notify('notifications.CLIENT_SPLIT.SAVE.RULE_INCOMPLETE', {
          type: 'warning',
          messageArgs: { index: i + 1 },
        });
        return;
      }

      if (r.ruleEntryMode === 'custom_sql') {
        if (!r.customSql || validateCustomSql(r.customSql) !== null) {
          notify('Invalid SQL in rule ' + (i + 1), { type: 'warning' });
          return;
        }
      } else if (r.sourceField === 'pharmacy') {
        if (!r.identifierType || !r.identifierValues || r.identifierValues.length === 0) {
          notify('notifications.CLIENT_SPLIT.SAVE.PHARMACY_REQUIRED', {
            type: 'warning',
            messageArgs: { index: i + 1 },
          });
          return;
        }
      } else {
        if (
          !r.sourceField ||
          r.matchValue === undefined ||
          isNaN(Number(r.conditionLength)) ||
          isNaN(Number(r.leftTrim)) ||
          isNaN(Number(r.rightTrim))
        ) {
          notify('notifications.CLIENT_SPLIT.SAVE.RULE_INCOMPLETE', {
            type: 'warning',
            messageArgs: { index: i + 1 },
          });
          return;
        }
      }
    }

    const finalRules: ClientSplitRulePayload[] = allRules.map((r) => {
      const base = {
        newClientName: r.newClientName,
        newClientNumber: r.newClientNumber,
        ruleEntryMode: r.ruleEntryMode || 'ui',
      };
      if (r.ruleEntryMode === 'custom_sql') {
        return { ...base, customSql: r.customSql };
      }
      if (r.sourceField === 'pharmacy') {
        return {
          ...base,
          sourceField: 'pharmacy',
          identifierType: r.identifierType,
          identifierValues: (r.identifierValues ?? []).map((p) => String(p.value)),
        };
      }
      return {
        ...base,
        sourceField: r.sourceField,
        matchValue: r.matchValue ?? '',
        matchOperator: r.matchOperator || 'equals',
        conditionLength: Number(r.conditionLength || 0),
        leftTrim: Number(r.leftTrim || 0),
        rightTrim: Number(r.rightTrim || 0),
      };
    });

    onSaveRule(finalRules).then(() => {
      setNewRules([]);

      setSelectedRows({});
      setPreviewResults([]);
      setActiveRuleIndex(null);

      if (listFilters) {
        setListFilters({ ...listFilters });
      }

      dataProvider.getOne('ClientSplitRules', { id: primaryClient.client_number }).then((res: any) => {
        const fetched = res?.data?.rules || [];

        const mapped = fetched.map((ruleRow: any) => {
          const config = ruleRow.extract_params || {};
          const sourceField = config.sourceField?.replace('orig_', '') || '';

          return {
            ...EMPTY_SPLIT_RULE,
            newClientName: config.newClientName,
            newClientNumber: config.newClientNumber,
            fieldToSplit: config.fieldToSplit || '',
            sourceField,
            matchValue: config.matchValue || '',
            conditionLength: Number(config.conditionLength) || 0,
            leftTrim: Number(config.leftTrim) || 0,
            rightTrim: Number(config.rightTrim) || 0,
            identifierType: config.identifierType,
            identifierValues: (config.identifierValues || []).map((v: string) => ({ name: v, value: v })),
            isExisting: true,
          };
        });

        setExistingRules(mapped);
      });
    });
  };

  const updateRule = (index: number, changes: Partial<ClientSplitRule>) => {
    const updated = [...newRules];
    updated[index] = { ...updated[index], ...changes };
    setNewRules(updated);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Preview Claims Data</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Load claims data to help you build accurate split rules.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label="Client"
            value={`${primaryClient.client_name} (${primaryClient.client_number})`}
            variant="standard"
            size="small"
            disabled
            sx={{ width: 400 }}
          />
          <TextField
            label="Financial Cutoff Date (From)"
            type="date"
            value={financialCutoffDate}
            onChange={(e) => setFinancialCutoffDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="standard"
            size="small"
            disabled={false}
            sx={{ width: 250 }}
          />
          <Button
            variant="contained"
            onClick={handleFetchPreview}
            startIcon={<SearchIcon />}
            sx={{ height: 40, mt: 1 }}
            disabled={!financialCutoffDate}
          >
            View Data
          </Button>
        </Box>

        {listFilters && (
          <Box sx={{ mt: 3 }}>
            <List
              resource="ClientCleanupPreview"
              filter={listFilters}
              pagination={<Pagination />}
              actions={false}
              empty={<CleanupEmpty />}
              sx={{ width: '100%', '& .RaList-content': { boxShadow: 'none' } }}
            >
              <Datagrid
                bulkActionButtons={false}
                size="small"
                sx={{
                  '& .MuiTableCell-head': { fontWeight: 'bold', fontSize: '0.9rem' },
                }}
              >
                <RaTextField source="client_name" label="Client Name" sortable={false} />
                <RaTextField source="medical_group_number" label="Medical Group Number" sortable={false} />
                <RaTextField source="location_number" label="Location Number" sortable={false} />
                <RaTextField source="division_number" label="Division Number" sortable={false} />
                <RaTextField source="pharmacy_nabp" label="NABP" />
                <RaTextField source="pharmacy_npi" label="NPI" />
                <RaTextField source="pharmacy_name" label="Pharmacy Name" />
              </Datagrid>
            </List>
          </Box>
        )}
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Preview Crosswalk Data
        </Typography>
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="medical_groups" />
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="locations" />
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="divisions" />
      </Paper>

      {existingRules.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Existing Rules
          </Typography>

          {existingRules.map((rule, index) => {
            const isPharmacy = rule.fieldToSplit === 'pharmacy';

            return (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setExistingRules(existingRules.filter((_, i) => i !== index))}
                  >
                    Delete
                  </Button>
                </Box>

                <Box
                  sx={{
                    pl: '48px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 6,
                    rowGap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ minWidth: 200 }}>
                    <strong>New Client Name</strong>
                    <div>{rule.newClientName}</div>
                  </Box>

                  <Box sx={{ minWidth: 160 }}>
                    <strong>New Client Number</strong>
                    <div>{rule.newClientNumber}</div>
                  </Box>

                  <Box sx={{ minWidth: 180 }}>
                    <strong>Field to Split</strong>
                    <div>{rule.fieldToSplit}</div>
                  </Box>

                  {!isPharmacy && (
                    <>
                      <Box sx={{ minWidth: 200 }}>
                        <strong>Source Field</strong>
                        <div>{rule.sourceField}</div>
                      </Box>

                      <Box sx={{ minWidth: 160 }}>
                        <strong>Match Value</strong>
                        <div>{rule.matchValue}</div>
                      </Box>

                      <Box sx={{ minWidth: 180 }}>
                        <strong>Extraction Rules</strong>
                        <div>
                          Left: {rule.leftTrim} &nbsp; Right: {rule.rightTrim}
                        </div>
                      </Box>

                      <Box sx={{ minWidth: 140 }}>
                        <strong>Minimum Length</strong>
                        <div>{rule.conditionLength}</div>
                      </Box>
                    </>
                  )}

                  {isPharmacy && (
                    <>
                      <Box sx={{ minWidth: 160 }}>
                        <strong>Identifier Type</strong>
                        <div>{rule.identifierType}</div>
                      </Box>

                      <Box sx={{ minWidth: 250 }}>
                        <strong>Values</strong>
                        <div>{(rule.identifierValues ?? []).map((v) => v.value).join(', ')}</div>
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Build Split Rules
        </Typography>

        {/* NEW: Summary List of added rules */}
        {newRules.map((rule, index) => (
          <Paper
            key={index}
            sx={{
              p: 1.5,
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#fcfcfc',
              border: '1px solid #eee',
            }}
          >
            <Typography variant="body2">{formatSplitRuleSummary(rule)}</Typography>
            <Box>
              <Button
                size="small"
                onClick={() => {
                  setDraftRule(rule);
                  setEditingRuleIndex(index);
                  setRuleEntryMode(rule.ruleEntryMode || 'ui');
                  setIsRuleDialogOpen(true);
                }}
              >
                Edit
              </Button>
              <IconButton
                size="small"
                color="error"
                onClick={() => setNewRules(newRules.filter((_, i) => i !== index))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}

        {/* Button to trigger the Dialog */}
        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => {
            setRuleEntryMode('ui');
            setDraftRule({
              ...EMPTY_SPLIT_RULE,
              newClientName: primaryClient.client_name + ' - ',
              newClientNumber: primaryClient.client_number,
              customSql: DEFAULT_SPLIT_SQL,
            });
            setEditingRuleIndex(null);
            setIsRuleDialogOpen(true);
          }}
        >
          + Add Rule
        </Button>

        <Button sx={{ ml: 2, mt: 1 }} variant="contained" onClick={handlePreviewRules} disabled={newRules.length === 0}>
          Preview Rules
        </Button>
        {previewAfterTable.length > 0 && (
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview After Split
            </Typography>

            <Datagrid
              resource="ClientCleanupPreview"
              data={previewAfterTable}
              total={previewAfterTable.length}
              isLoading={false}
              bulkActionButtons={false}
              size="small"
            >
              <RaTextField source="orig_client_number" label="Orig Client #" />
              <RaTextField source="orig_client_name" label="Orig Client Name" />

              <RaTextField source="client_number" label="Assigned Client #" />
              <RaTextField source="client_name" label="Assigned Client Name" />

              <RaTextField source="medical_group_number" label="Medical Group" />
              <RaTextField source="location_number" label="Location" />
              <RaTextField source="division_number" label="Division" />

              <RaTextField source="pharmacy_nabp" label="NABP" />
              <RaTextField source="pharmacy_npi" label="NPI" />
              <RaTextField source="pharmacy_name" label="Pharmacy Name" />
            </Datagrid>
          </Paper>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 4 }}>
          <Button variant="contained" disabled={isSaving || newRules.length === 0} onClick={handleSave} size="large">
            Save Client Split Rules
          </Button>
        </Box>
      </Paper>
      <Dialog open={isRuleDialogOpen} onClose={() => setIsRuleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRuleIndex !== null ? 'Edit Split Rule' : 'Add Split Rule'}</DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
            <TextField
              label="New Client Name"
              variant="standard"
              value={draftRule?.newClientName || ''}
              onChange={(e) => setDraftRule({ ...draftRule!, newClientName: e.target.value })}
            />
            <TextField
              label="New Client Number"
              variant="standard"
              value={draftRule?.newClientNumber || ''}
              onChange={(e) => setDraftRule({ ...draftRule!, newClientNumber: e.target.value })}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <ToggleButtonGroup
            value={ruleEntryMode}
            exclusive
            onChange={(_, v) => {
              if (!v) return;
              setRuleEntryMode(v);
              if (v === 'custom_sql' && !draftRule?.customSql) {
                setDraftRule({ ...draftRule!, customSql: DEFAULT_SPLIT_SQL });
              }
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="ui">Rule Builder</ToggleButton>
            <ToggleButton value="custom_sql">Manual SQL</ToggleButton>
          </ToggleButtonGroup>

          {ruleEntryMode === 'ui' && (
            <Box sx={{ display: 'grid', gap: 3, mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                  1. Identification Logic
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel>Identify Using Field</InputLabel>
                    <Select
                      value={draftRule?.sourceField || ''}
                      onChange={(e) => setDraftRule({ ...draftRule!, sourceField: e.target.value })}
                    >
                      <MenuItem value="medical_group_number">Medical Group</MenuItem>
                      <MenuItem value="location_number">Location</MenuItem>
                      <MenuItem value="division_number">Division</MenuItem>
                      <MenuItem value="pharmacy">Pharmacy</MenuItem>
                    </Select>
                  </FormControl>
                  {draftRule?.sourceField !== 'pharmacy' && (
                    <FormControl variant="standard" fullWidth>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={draftRule?.matchOperator || 'equals'}
                        onChange={(e) =>
                          setDraftRule({
                            ...draftRule!,
                            matchOperator: e.target.value as ClientSplitRule['matchOperator'],
                          })
                        }
                      >
                        <MenuItem value="contains">Contains </MenuItem>
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="not_equals">Does Not Equal</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {draftRule?.sourceField !== 'pharmacy' && (
                    <TextField
                      fullWidth
                      label="Match Value"
                      variant="standard"
                      value={draftRule?.matchValue || ''}
                      onChange={(e) => setDraftRule({ ...draftRule!, matchValue: e.target.value })}
                      placeholder="e.g. 001"
                    />
                  )}

                  {draftRule?.sourceField === 'pharmacy' && (
                    <FormControl variant="standard" fullWidth>
                      <InputLabel>Identifier Type</InputLabel>
                      <Select
                        value={draftRule?.identifierType || ''}
                        onChange={(e) =>
                          setDraftRule({ ...draftRule!, identifierType: e.target.value as 'npi' | 'nabp' })
                        }
                      >
                        <MenuItem value="npi">NPI</MenuItem>
                        <MenuItem value="nabp">NABP</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {draftRule?.sourceField === 'pharmacy' && (
                  <Box sx={{ mt: 2 }}>
                    <Autocomplete<PharmacyOption, true>
                      multiple
                      open={pharmacyOpen}
                      onOpen={() => setPharmacyOpen(true)}
                      onClose={() => setPharmacyOpen(false)}
                      disabled={!draftRule?.identifierType}
                      options={pharmacyOptions}
                      loading={pharmacyLoading}
                      getOptionLabel={(p) => `${p.value} (${p.name})`}
                      value={draftRule?.identifierValues ?? []}
                      onChange={(_, selectedList) => setDraftRule({ ...draftRule!, identifierValues: selectedList })}
                      onInputChange={(_, value, reason) => {
                        if (reason !== 'input') return;
                        if (value.trim().length >= 2 && draftRule?.identifierType) {
                          debouncedSearch(draftRule.identifierType, value);
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label="Search Pharmacy" variant="standard" />}
                    />
                  </Box>
                )}
              </Paper>

              {draftRule?.sourceField !== 'pharmacy' && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                    2. Extraction Logic (Optional)
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ mb: 2, display: 'block', fontStyle: 'italic' }}>
                    Tip: Trims will be applied to the <strong>field</strong> before comparing to the Match Value.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField
                      label="Min Length"
                      variant="standard"
                      value={draftRule?.conditionLength || ''}
                      onChange={(e) => setDraftRule({ ...draftRule!, conditionLength: e.target.value })}
                    />
                    <TextField
                      label="Take Left"
                      variant="standard"
                      value={draftRule?.leftTrim || ''}
                      onChange={(e) => setDraftRule({ ...draftRule!, leftTrim: e.target.value })}
                    />
                    <TextField
                      label="Take Right"
                      variant="standard"
                      value={draftRule?.rightTrim || ''}
                      onChange={(e) => setDraftRule({ ...draftRule!, rightTrim: e.target.value })}
                    />
                  </Box>
                </Paper>
              )}
            </Box>
          )}
          {ruleEntryMode === 'custom_sql' && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Manual SQL Mode
                </Typography>
                <Typography variant="body2">
                  Only single <strong>UPDATE</strong> statements using the <strong>{'{{TABLE}}'}</strong> placeholder
                  are permitted. This rule bypasses automated validations. Use with caution.
                </Typography>
              </Alert>

              <TextField
                label="Custom SQL Query"
                multiline
                minRows={10}
                fullWidth
                variant="outlined"
                value={draftRule?.customSql || ''}
                error={!!customSqlError}
                helperText={customSqlError || ' '}
                onChange={(e) => setDraftRule({ ...draftRule!, customSql: e.target.value })}
                placeholder={`UPDATE {{TABLE}} SET client_number = '09999' WHERE client_number = '${primaryClient.client_number}' AND ...`}
                sx={{
                  fontFamily: 'monospace',
                  '& textarea': { fontSize: '0.9rem', lineHeight: 1.6 },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsRuleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!isDraftValid(draftRule)} onClick={handleSaveDraftToRulesList}>
            Add Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientSplitForm;
